import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { AppContext } from '../App';
import { 
  Droplets, 
  ChevronRight, 
  MapPin, 
  User, 
  Phone, 
  Mail, 
  FileText,
  CheckCircle,
  ArrowRight,
  Shield,
  Clock,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const ForageEauDemande = () => {
  const { API } = useContext(AppContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    code_postal: '',
    commune: '',
    type_projet: '',
    nom: '',
    telephone: '',
    email: '',
    besoin_details: '',
    rgpd_accepted: false
  });

  const typesProjets = [
    { value: 'arrosage_jardin', label: 'Arrosage jardin' },
    { value: 'piscine', label: 'Piscine' },
    { value: 'usage_maison', label: 'Usage maison (sanitaires, lave-linge...)' },
    { value: 'agricole', label: 'Agricole / Irrigation' },
    { value: 'autre', label: 'Autre' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.code_postal.trim()) {
      toast.error('Le code postal est obligatoire');
      return;
    }
    if (!formData.type_projet) {
      toast.error('Veuillez sélectionner un type de projet');
      return;
    }
    if (!formData.nom.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }
    if (!formData.telephone.trim()) {
      toast.error('Le téléphone est obligatoire');
      return;
    }
    if (!formData.rgpd_accepted) {
      toast.error('Veuillez accepter la politique de confidentialité');
      return;
    }

    setLoading(true);
    
    try {
      const form = new FormData();
      form.append('code_postal', formData.code_postal.trim());
      form.append('commune', formData.commune.trim());
      form.append('type_projet', formData.type_projet);
      form.append('nom', formData.nom.trim());
      form.append('telephone', formData.telephone.trim());
      if (formData.email) form.append('email', formData.email.trim());
      if (formData.besoin_details) form.append('besoin_details', formData.besoin_details.trim());
      form.append('rgpd_accepted', formData.rgpd_accepted);

      await axios.post(`${API}/demandes/forage-eau`, form);
      
      setSubmitted(true);
      toast.success('Demande envoyée avec succès !');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // Success screen
  if (submitted) {
    return (
      <>
        <Helmet>
          <title>Demande envoyée | Forage d'eau | GEOTS Consulting</title>
        </Helmet>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-[#0A2540] mb-4">
                Demande envoyée !
              </h1>
              <p className="text-gray-600 mb-6">
                Nous avons bien reçu votre demande de forage d'eau. Un conseiller vous contactera 
                dans les plus brefs délais pour étudier votre projet.
              </p>
              <div className="flex flex-col gap-3">
                <Link 
                  to="/guides/prix-forage-eau"
                  className="text-[#00D4FF] hover:underline font-medium"
                >
                  ← Retour au guide Prix forage d'eau
                </Link>
                <Link 
                  to="/"
                  className="text-gray-500 hover:text-gray-700"
                >
                  Retour à l'accueil
                </Link>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Demande de forage d'eau | Trouver un professionnel | GEOTS Consulting</title>
        <meta name="description" content="Demandez un devis gratuit pour votre projet de forage d'eau. GEOTS vous met en relation avec des foreurs qualifiés près de chez vous." />
        <link rel="canonical" href="https://geots.consulting/forage-eau/demande" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-[#635BFF]">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/guides/prix-forage-eau" className="hover:text-[#635BFF]">Guide forage d'eau</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0A2540] font-medium">Demande de devis</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <header className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] text-white py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="bg-[#00D4FF]/20 p-3 rounded-xl">
                  <Droplets className="w-8 h-8 text-[#00D4FF]" />
                </div>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-4">
                Trouver un professionnel pour mon forage d'eau
              </h1>
              <p className="text-gray-300 text-lg">
                Remplissez ce formulaire et recevez des devis de foreurs qualifiés près de chez vous.
              </p>
            </div>
          </div>
        </header>

        {/* Form Section */}
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto">
            
            {/* Trust badges */}
            <div className="flex flex-wrap justify-center gap-6 mb-8">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Shield className="w-5 h-5 text-green-500" />
                <span>100% gratuit</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="w-5 h-5 text-[#00D4FF]" />
                <span>Réponse sous 48h</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle className="w-5 h-5 text-[#635BFF]" />
                <span>Sans engagement</span>
              </div>
            </div>

            {/* Form Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* Location Section */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-[#0A2540] flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-[#00D4FF]" />
                    Localisation du projet
                  </h2>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Code postal <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="code_postal"
                        value={formData.code_postal}
                        onChange={handleChange}
                        placeholder="Ex: 75001"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D4FF] focus:border-transparent transition-all"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Commune
                      </label>
                      <input
                        type="text"
                        name="commune"
                        value={formData.commune}
                        onChange={handleChange}
                        placeholder="Ex: Paris"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D4FF] focus:border-transparent transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* Project Type */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-[#0A2540] flex items-center gap-2">
                    <Droplets className="w-5 h-5 text-[#00D4FF]" />
                    Type de projet
                  </h2>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Usage prévu de l'eau <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="type_projet"
                      value={formData.type_projet}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D4FF] focus:border-transparent transition-all bg-white"
                      required
                    >
                      <option value="">Sélectionnez un usage</option>
                      {typesProjets.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-[#0A2540] flex items-center gap-2">
                    <User className="w-5 h-5 text-[#00D4FF]" />
                    Vos coordonnées
                  </h2>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      placeholder="Votre nom"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D4FF] focus:border-transparent transition-all"
                      required
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Téléphone <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="tel"
                          name="telephone"
                          value={formData.telephone}
                          onChange={handleChange}
                          placeholder="06 12 34 56 78"
                          className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D4FF] focus:border-transparent transition-all"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="votre@email.com"
                          className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D4FF] focus:border-transparent transition-all"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-[#0A2540] flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#00D4FF]" />
                    Précisez votre besoin
                  </h2>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description (facultatif)
                    </label>
                    <textarea
                      name="besoin_details"
                      value={formData.besoin_details}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Décrivez votre projet : surface du terrain, usage prévu, contraintes particulières..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D4FF] focus:border-transparent transition-all resize-none"
                    />
                  </div>
                </div>

                {/* RGPD */}
                <div className="border-t pt-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="rgpd_accepted"
                      checked={formData.rgpd_accepted}
                      onChange={handleChange}
                      className="mt-1 w-5 h-5 rounded border-gray-300 text-[#00D4FF] focus:ring-[#00D4FF]"
                      required
                    />
                    <span className="text-sm text-gray-600">
                      J'accepte que mes données soient traitées par GEOTS Consulting pour répondre à ma demande, 
                      conformément à la <Link to="/privacy-policy" className="text-[#00D4FF] hover:underline">politique de confidentialité</Link>. 
                      <span className="text-red-500">*</span>
                    </span>
                  </label>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#00D4FF] to-[#0099cc] hover:from-[#00b8e0] hover:to-[#0088bb] text-white font-semibold py-4 px-6 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      Envoyer ma demande
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Bottom info */}
            <p className="text-center text-sm text-gray-500 mt-6">
              Vos données sont protégées et ne seront jamais revendues. 
              Aucun compte n'est créé automatiquement.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForageEauDemande;
